import { useState, useEffect } from "react";
import {
  Star,
  ThumbsUp,
  ChevronDown,
  CheckCircle,
  X,
  Camera,
  MessageSquare,
  Award,
  Plus,
  Minus,
  Image as ImageIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import BaseApi from "../../services/base-api";
import { LocalizedText } from "../../types/LocalizedText";

interface Product {
  id: string | number;
  rating?: number | null;
  ratingCount?: number;
  name?: LocalizedText;
}

type ReviewPayload = {
  productId: number | string;
  orderId: string;
  rating: number;
  isRecommended: boolean;
  title?: string;
  review?: string;
  pros?: string[];
  cons?: string[];
  images?: File[]; // or string[] depending on backend
};

interface Review {
  id: string;
  userId: string;
  rating: number;
  title: string | null;
  review: string | null;
  pros: string | null;
  cons: string | null;
  isRecommended: boolean;
  images: string[];
  isApproved: boolean;
  adminResponse: string | null;
  helpfulCount: number;
  notHelpfulCount: number;
  createdAt: string;
  user: {
    firstName: string;
    lastName: string;
  };
}

interface RatingDistribution {
  1: number;
  2: number;
  3: number;
  4: number;
  5: number;
}

interface ProductRatingProps {
  product: Product;
  orderId?: string; // Required if user is rating after purchase
  showReviews?: boolean;
  allowRating?: boolean;
  mode?: "compact" | "full"; // compact for product cards, full for product details page
  size?: "sm" | "md" | "lg";
  showCount?: boolean;
  onRatingUpdate?: (rating: number) => void;
}

const ProductRating: React.FC<ProductRatingProps> = ({
  product,
  orderId,
  showReviews = true,
  allowRating = true,
  mode = "full",
  size = "md",
  showCount = true,
  onRatingUpdate,
}) => {
  const { t } = useTranslation();
  const { success, err } = useToast();

  // Rating states
  const [canRate, setCanRate] = useState(false);
  const [myRating, setMyRating] = useState<number | null>(null);
  const [hasSubmittedRating, setHasSubmittedRating] = useState(false); // New state to track if rating is submitted
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(false);

  // Review form states
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [pros, setPros] = useState<string[]>([""]);
  const [cons, setCons] = useState<string[]>([""]);
  const [isRecommended, setIsRecommended] = useState<boolean>(true);
  const [reviewImages, setReviewImages] = useState<File[]>([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reviews list states
  const [reviews, setReviews] = useState<Review[]>([]);
  const [distribution, setDistribution] = useState<RatingDistribution | null>(
    null,
  );
  const [filterRating, setFilterRating] = useState<number | "all">("all");
  const [loadingReviews, setLoadingReviews] = useState(false);

  // Size configurations
  const sizeConfig = {
    sm: { star: "w-4 h-4", text: "text-xs", gap: "gap-0.5" },
    md: { star: "w-5 h-5", text: "text-sm", gap: "gap-1" },
    lg: { star: "w-6 h-6", text: "text-base", gap: "gap-1.5" },
  };

  const config = sizeConfig[size];

  useEffect(() => {
    checkCanRate();
    if (showReviews && mode === "full") {
      loadReviews();
    }
  }, [product.id]);

  const checkCanRate = async () => {
    try {
      const res = await BaseApi.get(`/ratings/can-rate/${product.id}`);
      if (res.success) {
        setCanRate(res.data.canRate);
        if (res.data.existingRating) {
          setMyRating(res.data.existingRating.rating);
          setHasSubmittedRating(true); // Mark as submitted
          setReviewTitle(res.data.existingRating.title || "");
          setReviewText(res.data.existingRating.review || "");
          setPros(
            res.data.existingRating.pros?.split("\n").filter(Boolean) || [""],
          );
          setCons(
            res.data.existingRating.cons?.split("\n").filter(Boolean) || [""],
          );
          setIsRecommended(res.data.existingRating.isRecommended ?? true);
        }
      }
    } catch (error) {
      console.error("Error checking rating eligibility:", error);
    }
  };

  const loadReviews = async () => {
    setLoadingReviews(true);
    try {
      const res = await BaseApi.get(`/ratings/product/${product.id}`);
      if (res.success && res.data) {
        setReviews(res.data.reviews || res.data || []);
        setDistribution(res.data.distribution || null);
      }
    } catch (error) {
      console.error("Error loading reviews:", error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleRate = async (value: number) => {
    if (!allowRating || submitting) return;

    setMyRating(value);

    // In compact mode, submit rating immediately without review form
    if (mode === "compact" && canRate) {
      await submitRating(value);
    } else if (mode === "full" && canRate && !hasSubmittedRating) {
      // In full mode, just show the review form - don't submit yet
      setShowReviewForm(true);
    }
  };

  const submitRating = async (ratingValue?: number) => {
    const finalRating = ratingValue || myRating;
    if (!finalRating) {
      err(t("Please select a rating"));
      return;
    }

    if (!orderId) {
      err(t("Order ID is required to submit rating"));
      return;
    }

    setSubmitting(true);
    try {
      const payload: ReviewPayload = {
        productId: product.id,
        orderId,
        rating: finalRating,
        isRecommended,
      };

      // optional fields
      if (reviewTitle.trim()) {
        payload.title = reviewTitle.trim();
      }

      if (reviewText.trim()) {
        payload.review = reviewText.trim();
      }

      const prosText = pros.filter((p) => p.trim());
      if (prosText.length) {
        payload.pros = prosText;
      }

      const consText = cons.filter((c) => c.trim());
      if (consText.length) {
        payload.cons = consText;
      }

      if (reviewImages.length) {
        payload.images = reviewImages;
      }

      const res = await BaseApi.post("/ratings", payload);

      if (res.success) {
        success(
          t(
            "Review submitted successfully! It will be visible after approval.",
          ),
        );
        setShowReviewForm(false);
        setHasSubmittedRating(true); // Mark as submitted
        resetForm();

        // Call onRatingUpdate callback if provided
        if (onRatingUpdate) {
          onRatingUpdate(finalRating);
        }

        if (mode === "full") {
          loadReviews();
        }
      }
    } catch (error: any) {
      console.error("Error submitting review:", error);
      err(error.message || t("Failed to submit review"));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setReviewTitle("");
    setReviewText("");
    setPros([""]);
    setCons([""]);
    setIsRecommended(true);
    setReviewImages([]);
  };

  const handleHelpful = async (reviewId: string, isHelpful: boolean) => {
    try {
      const res = await BaseApi.post(`/ratings/${reviewId}/helpful`, {
        isHelpful,
      });
      if (res.success) {
        success(t(isHelpful ? "Marked as helpful" : "Marked as not helpful"));
        loadReviews();
      }
    } catch (error) {
      console.error("Error marking review:", error);
      err(t("Failed to mark review"));
    }
  };

  const addProField = () => {
    if (pros.length < 5) {
      setPros([...pros, ""]);
    }
  };

  const removeProField = (index: number) => {
    if (pros.length > 1) {
      setPros(pros.filter((_, i) => i !== index));
    }
  };

  const updateProField = (index: number, value: string) => {
    const newPros = [...pros];
    newPros[index] = value;
    setPros(newPros);
  };

  const addConField = () => {
    if (cons.length < 5) {
      setCons([...cons, ""]);
    }
  };

  const removeConField = (index: number) => {
    if (cons.length > 1) {
      setCons(cons.filter((_, i) => i !== index));
    }
  };

  const updateConField = (index: number, value: string) => {
    const newCons = [...cons];
    newCons[index] = value;
    setCons(newCons);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remainingSlots = 5 - reviewImages.length;

    if (files.length > remainingSlots) {
      err(t("Maximum 5 images allowed"));
      const allowedFiles = files.slice(0, remainingSlots);
      setReviewImages([...reviewImages, ...allowedFiles]);
      return;
    }

    setReviewImages([...reviewImages, ...files]);
  };

  const removeImage = (index: number) => {
    setReviewImages(reviewImages.filter((_, i) => i !== index));
  };

  const averageRating = product.rating || 0;
  const ratingCount = product.ratingCount || reviews.length || 0;

  const filteredReviews =
    filterRating === "all"
      ? reviews
      : reviews.filter((r) => r.rating === filterRating);

  const getDistributionPercentage = (stars: number) => {
    if (!distribution || ratingCount === 0) return 0;
    return (
      ((distribution[stars as keyof RatingDistribution] || 0) / ratingCount) *
      100
    );
  };

  if (mode === "compact") {
    return (
      <div
        className={`flex items-center ${config.gap}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRate(value);
              }}
              onMouseEnter={() => allowRating && setHoverRating(value)}
              onMouseLeave={() => setHoverRating(0)}
              disabled={!allowRating || !canRate || submitting}
              className={`transition-all ${
                allowRating && canRate
                  ? "hover:scale-110 cursor-pointer"
                  : "cursor-default"
              } disabled:opacity-50`}
            >
              <Star
                className={`${config.star} ${
                  value <= (hoverRating || myRating || averageRating)
                    ? "text-yellow-400 fill-yellow-400"
                    : "text-gray-300"
                }`}
              />
            </button>
          ))}
        </div>
        {showCount && (
          <span className={`${config.text} text-gray-600 font-medium`}>
            ({ratingCount})
          </span>
        )}
      </div>
    );
  }

  // Full mode
  return (
    <div className="w-full space-y-4" onClick={(e) => e.stopPropagation()}>
      {/* Rating Summary */}
      <div className="bg-white rounded-xl shadow-md p-2 sm:p-3 border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-6">
          {/* Average Rating */}
          <div className="flex flex-row justify-between items-center gap-1">
            <div className="flex flex-row text-xl font-bold text-gray-900 gap-2">
              <Star className="w-7 h-7 text-yellow-400 fill-yellow-400 shrink-0" />
              {averageRating.toFixed(1)}
            </div>
            <div className="flex items-center justify-center sm:justify-start gap-1">
              {[1, 2, 3, 4, 5].map((value) => (
                <Star
                  key={value}
                  className={`w-5 h-5 ${
                    value <= Math.round(averageRating)
                      ? "text-yellow-400 fill-yellow-400"
                      : "text-gray-300"
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-gray-600">
              ({ratingCount} {ratingCount === 1 ? t("review") : t("reviews")})
            </p>
          </div>

          {/* Distribution */}
          {distribution && (
            <div className="flex-1 space-y-2">
              {[5, 4, 3, 2, 1].map((stars) => (
                <button
                  key={stars}
                  onClick={() => setFilterRating(stars)}
                  className="w-full flex items-center gap-3 hover:bg-gray-50 rounded-lg p-1 transition-colors group"
                >
                  <span className="text-sm font-medium text-gray-700 w-12">
                    {stars} {t("star")}
                  </span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-yellow-400 h-full transition-all duration-300 group-hover:bg-yellow-500"
                      style={{ width: `${getDistributionPercentage(stars)}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 w-12 text-right">
                    {distribution[stars as keyof RatingDistribution] || 0}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rate this Product - Only show if user hasn't submitted a rating yet */}
      {allowRating && canRate && !hasSubmittedRating && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl shadow-md p-4 sm:p-6 border-2 border-yellow-200">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-600" />
            {t("Rate this product")}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {t("Share your thoughts with other customers")}
          </p>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => handleRate(value)}
                onMouseEnter={() => setHoverRating(value)}
                onMouseLeave={() => setHoverRating(0)}
                disabled={submitting}
                className="transition-transform hover:scale-125 disabled:opacity-50"
              >
                <Star
                  className={`w-10 h-10 ${
                    value <= (hoverRating || myRating || 0)
                      ? "text-yellow-400 fill-yellow-400"
                      : "text-gray-300"
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Already Rated - Only show if user has submitted a rating */}
      {hasSubmittedRating && myRating && (
        <div className="bg-green-50 rounded-xl shadow-md p-4 border-2 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-green-700 mb-1 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {t("You've rated this product")}
              </p>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((value) => (
                  <Star
                    key={value}
                    className={`w-5 h-5 ${
                      value <= myRating
                        ? "text-yellow-400 fill-yellow-400"
                        : "text-gray-300"
                    }`}
                  />
                ))}
              </div>
            </div>
            <button
              onClick={() => setShowReviewForm(true)}
              className="px-4 py-2 bg-white border-2 border-green-300 text-green-700 rounded-lg hover:bg-green-50 transition-colors font-semibold text-sm"
            >
              {t("Edit Review")}
            </button>
          </div>
        </div>
      )}

      {/* Review Form */}
      {showReviewForm && (
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-2 border-yellow-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">
              {t("Write Your Review")}
            </h3>
            <button
              onClick={() => {
                setShowReviewForm(false);
                // If user hasn't submitted yet and closes the form, reset the rating
                if (!hasSubmittedRating) {
                  setMyRating(null);
                }
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Rating Stars */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Your Rating")} <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMyRating(value)}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-10 h-10 ${
                        value <= (myRating || 0)
                          ? "text-yellow-400 fill-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Review Title")}
              </label>
              <input
                type="text"
                value={reviewTitle}
                onChange={(e) => setReviewTitle(e.target.value)}
                placeholder={t("Sum up your experience in one line")}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-yellow-500 focus:outline-none text-sm"
                maxLength={100}
              />
            </div>

            {/* Review Text */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Your Review")}
              </label>
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                rows={5}
                placeholder={t("Tell us what you think about this product...")}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-yellow-500 focus:outline-none resize-none text-sm"
                maxLength={1000}
              />
              <p className="text-xs text-gray-500 mt-1">
                {reviewText.length}/1000 {t("characters")}
              </p>
            </div>

            {/* Pros */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Plus className="w-4 h-4 text-green-600" />
                {t("What did you like?")}
              </label>
              <div className="space-y-2">
                {pros.map((pro, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={pro}
                      onChange={(e) => updateProField(index, e.target.value)}
                      placeholder={t("e.g., Great quality, Fast shipping")}
                      className="flex-1 px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none text-sm"
                      maxLength={100}
                    />
                    {pros.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeProField(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                {pros.length < 5 && (
                  <button
                    type="button"
                    onClick={addProField}
                    className="flex items-center gap-2 text-sm text-green-600 hover:text-green-700 font-medium px-2 py-1 hover:bg-green-50 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {t("Add another pro")}
                  </button>
                )}
              </div>
            </div>

            {/* Cons */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Minus className="w-4 h-4 text-red-600" />
                {t("What could be better?")}
              </label>
              <div className="space-y-2">
                {cons.map((con, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={con}
                      onChange={(e) => updateConField(index, e.target.value)}
                      placeholder={t("e.g., Packaging, Size runs small")}
                      className="flex-1 px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none text-sm"
                      maxLength={100}
                    />
                    {cons.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeConField(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                {cons.length < 5 && (
                  <button
                    type="button"
                    onClick={addConField}
                    className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 font-medium px-2 py-1 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {t("Add another con")}
                  </button>
                )}
              </div>
            </div>

            {/* Recommendation */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="recommend"
                checked={isRecommended}
                onChange={(e) => setIsRecommended(e.target.checked)}
                className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
              />
              <label
                htmlFor="recommend"
                className="text-sm font-medium text-gray-700 cursor-pointer"
              >
                {t("I recommend this product")}
              </label>
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                {t("Add Photos")} ({reviewImages.length}/5)
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {reviewImages.map((img, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={URL.createObjectURL(img)}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {reviewImages.length < 5 && (
                  <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-yellow-500 hover:bg-yellow-50 transition-all">
                    <Camera className="w-6 h-6 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">{t("Add")}</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {t("Upload up to 5 photos (JPG, PNG)")}
              </p>
            </div>

            {/* Submit Button */}
            <button
              onClick={() => submitRating()}
              disabled={!myRating || submitting}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-4 rounded-xl hover:shadow-lg transition-all font-bold text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t("Submitting...")}
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  {t("Submit Review")}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Reviews List */}
      {showReviews && !!ratingCount && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen((v) => !v);
            }}
            className="w-full flex items-center justify-between px-6 py-2 bg-white rounded-xl border-2 border-gray-200 shadow-sm hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3">
              <Award className="w-6 h-6 text-yellow-600" />
              <h3 className="text-sm font-bold text-gray-900">
                {t("Customer Reviews")}
              </h3>
              <span className="text-sm text-gray-500">({ratingCount})</span>
            </div>

            <ChevronDown
              className={`w-6 h-6 text-gray-500 transition-transform duration-300 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Reviews List */}
          <div
            className={`overflow-hidden transition-all duration-500 ease-in-out ${
              isOpen ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <div className="space-y-4 mt-4">
              {/* Filter Clear */}
              {filterRating !== "all" && (
                <div className="flex items-center justify-between bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                  <span className="text-sm text-gray-700">
                    {t("Showing")} {filterRating}-{t("star reviews")}
                  </span>
                  <button
                    onClick={() => setFilterRating("all")}
                    className="flex items-center gap-1 text-sm text-yellow-700 hover:text-yellow-800 font-semibold"
                  >
                    <X className="w-4 h-4" />
                    {t("Clear Filter")}
                  </button>
                </div>
              )}

              {loadingReviews ? (
                <div className="bg-white rounded-xl shadow-md p-12 text-center border border-gray-200">
                  <div className="w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-sm text-gray-600">
                    {t("Loading reviews...")}
                  </p>
                </div>
              ) : filteredReviews.length === 0 ? (
                <div className="bg-white rounded-xl shadow-md p-12 text-center border border-gray-200">
                  <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-700 mb-2">
                    {filterRating !== "all"
                      ? t("No reviews with this rating")
                      : t("No reviews yet")}
                  </p>
                  <p className="text-sm text-gray-500">
                    {t("Be the first to review this product!")}
                  </p>
                </div>
              ) : (
                filteredReviews.map((review) => (
                  <div
                    key={review.id}
                    className="bg-white rounded-xl shadow-md p-5 sm:p-6 border border-gray-200 hover:shadow-lg transition-shadow"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <p className="font-bold text-gray-900">
                            {review.user.firstName} {review.user.lastName}
                          </p>
                          {review.isRecommended && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              <CheckCircle className="w-3 h-3" />
                              {t("Recommends")}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {new Date(review.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            },
                          )}
                        </p>
                      </div>

                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i < review.rating
                                ? "text-yellow-400 fill-yellow-400"
                                : "text-gray-300"
                            }`}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Title */}
                    {review.title && (
                      <h4 className="font-bold text-gray-900 mb-2 text-base">
                        {review.title}
                      </h4>
                    )}

                    {/* Review */}
                    {review.review && (
                      <p className="text-sm text-gray-700 leading-relaxed mb-3">
                        {review.review}
                      </p>
                    )}

                    {/* Pros / Cons */}
                    {(review.pros || review.cons) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                        {review.pros && (
                          <div className="bg-green-50 rounded-lg p-3 border-l-4 border-green-400">
                            <p className="text-xs font-bold text-green-700 mb-2 flex items-center gap-1">
                              <Plus className="w-3 h-3" />
                              {t("Pros")}
                            </p>
                            <ul className="text-sm text-gray-700 space-y-1">
                              {review.pros.split("\n").map((pro, idx) => (
                                <li key={idx} className="flex gap-2">
                                  <span className="text-green-600 font-bold">
                                    •
                                  </span>
                                  <span>{pro}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {review.cons && (
                          <div className="bg-red-50 rounded-lg p-3 border-l-4 border-red-400">
                            <p className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1">
                              <Minus className="w-3 h-3" />
                              {t("Cons")}
                            </p>
                            <ul className="text-sm text-gray-700 space-y-1">
                              {review.cons.split("\n").map((con, idx) => (
                                <li key={idx} className="flex gap-2">
                                  <span className="text-red-600 font-bold">
                                    •
                                  </span>
                                  <span>{con}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Images */}
                    {review.images?.length > 0 && (
                      <div className="flex gap-2 mb-3 flex-wrap">
                        {review.images.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`Review ${idx + 1}`}
                            className="w-20 h-20 rounded-lg object-cover border-2 border-gray-200 hover:scale-105 transition-transform cursor-pointer"
                            onClick={() => window.open(img, "_blank")}
                          />
                        ))}
                      </div>
                    )}

                    {/* Admin Response */}
                    {review.adminResponse && (
                      <div className="bg-blue-100 rounded-lg p-4 mb-3 border-l-4 border-blue-400">
                        <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          {t("Response from")} {product.name?.en || "Store"}
                        </p>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {review.adminResponse}
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-4 pt-3 border-t border-gray-600">
                      <button
                        onClick={() => handleHelpful(review.id, true)}
                        className="flex items-center gap-2 text-sm text-gray-600 hover:text-green-600 transition-colors font-medium"
                      >
                        <ThumbsUp className="w-4 h-4" />
                        <span>{t("Helpful")}</span>
                        {review.helpfulCount > 0 && (
                          <span className="font-bold">
                            ({review.helpfulCount})
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductRating;
